import { Box, Flex, Grid, Stack, Text } from "@chakra-ui/react";
import type { BotRecord } from "../types/domain";
import { StatusPill } from "./ui";

type Tone = "positive" | "negative" | "warning" | "info" | "neutral";

interface Condition {
  label: string;
  value: string;
  status: string;
  detail: string;
  tone: Tone;
}

function numeric(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function pct(value: number | undefined): string {
  return value === undefined ? "Not observed" : `${(value * 100).toFixed(4)}%`;
}

function strategyClockLabel(config: any): string {
  const zone = typeof config?.strategyTimeZone === "string" ? config.strategyTimeZone : "UTC";
  if (zone === "Europe/Athens") return "legacy dataset clock (EU EET/EEST)";
  if (zone === "Pepperstone/Server") return "Pepperstone server (GMT+2/+3, US DST)";
  return zone;
}

function target(label: string, raw: unknown): Condition {
  const value = numeric(raw);
  if (value === undefined) return { label, value: "Not observed", status: "WAITING", detail: "No target has been persisted for this sleeve yet.", tone: "warning" };
  if (value > 1e-12) return { label, value: `+${value.toFixed(3)}x`, status: "LONG", detail: "Strategy currently requests long exposure.", tone: "positive" };
  if (value < -1e-12) return { label, value: `${value.toFixed(3)}x`, status: "SHORT", detail: "Strategy currently requests short exposure.", tone: "negative" };
  return { label, value: "0.000x", status: "FLAT", detail: "This sleeve currently requests no exposure.", tone: "neutral" };
}

function lossStreakGovernorRows(state: any, config: any): Condition[] {
  const enabled = config?.lossStreakGovernorEnabled !== false;
  const trigger = numeric(config?.lossStreakGovernorTriggerLosses) ?? 3;
  const reducedScale = numeric(config?.lossStreakGovernorScale) ?? 0.25;
  const streak = numeric(state?.consecutiveNetLosingTrades) ?? 0;
  const maximum = numeric(state?.maxConsecutiveNetLosingTrades) ?? streak;
  const scaledEntries = numeric(state?.lossStreakScaledEntries) ?? 0;
  const lastPnl = numeric(state?.lastClosedTradeNetPnl);
  const active = enabled && streak >= trigger;
  return [
    {
      label: "Consecutive net losing trades",
      value: `${streak.toFixed(0)} / ${trigger.toFixed(0)} (max ${maximum.toFixed(0)})`,
      status: !enabled ? "DISABLED" : active ? "ACTIVE" : "MONITORING",
      detail: "Counted only after the broker position is flat. A net winner or effectively-flat result resets the streak.",
      tone: !enabled ? "neutral" : active ? "warning" : "info",
    },
    {
      label: "Next-entry sizing",
      value: active ? `${(reducedScale * 100).toFixed(0)}% of sleeve target` : "100% of sleeve target",
      status: active ? "REDUCED" : "FULL SIZE",
      detail: active
        ? "Only brand-new entries are scaled. Existing positions and exits are never enlarged or delayed by this governor."
        : `Sizing reduces after ${trigger.toFixed(0)} consecutive net losing closed trades.`,
      tone: active ? "warning" : "positive",
    },
    {
      label: "Last closed trade (net)",
      value: lastPnl === undefined ? "Not observed" : `${lastPnl >= 0 ? "+" : ""}${lastPnl.toFixed(2)} account units`,
      status: lastPnl === undefined ? "WAITING" : lastPnl < 0 ? "LOSS" : "RESET",
      detail: "Cash-flow-adjusted equity change; detected deposits and withdrawals do not turn a loss into a win.",
      tone: lastPnl === undefined ? "neutral" : lastPnl < 0 ? "negative" : "positive",
    },
    {
      label: "Reduced entries since state creation",
      value: scaledEntries.toFixed(0),
      status: scaledEntries > 0 ? "OBSERVED" : "NONE",
      detail: "Persistent audit counter for entries opened while the governor was active.",
      tone: scaledEntries > 0 ? "info" : "neutral",
    },
  ];
}

function momentumObservation(state: any): { ready: boolean; value?: number; day?: string } {
  const start = numeric(state?.signalStartOpen);
  const end = numeric(state?.signalEndClose);
  const day = typeof state?.signalStartDay === "string" ? state.signalStartDay : undefined;
  const ready = Boolean(start && start > 0 && end && end > 0 && day && state?.signalEndDay === day);
  return { ready, value: ready ? end! / start! - 1 : undefined, day };
}

function currentP16Candidate(p16: any): { low: boolean; high: boolean } {
  const bar = p16?.features?.lastFinalizedBar;
  const history = Array.isArray(p16?.features?.history) ? p16.features.history : [];
  const openTime = numeric(bar?.openTimeMs);
  if (!bar || openTime === undefined) return { low: false, high: false };
  const prior = history
    .filter((item: any) => numeric(item?.openTimeMs) !== undefined && item.openTimeMs < openTime)
    .slice(-16);
  if (prior.length < 16) return { low: false, high: false };
  const lows = prior.map((item: any) => numeric(item.low)).filter((item: number | undefined): item is number => item !== undefined);
  const highs = prior.map((item: any) => numeric(item.high)).filter((item: number | undefined): item is number => item !== undefined);
  if (lows.length !== 16 || highs.length !== 16) return { low: false, high: false };
  return {
    low: numeric(bar.low)! <= Math.min(...lows),
    high: numeric(bar.high)! >= Math.max(...highs),
  };
}

function p16Rows(p16: any, config: any): Condition[] {
  const trend = numeric(p16?.features?.lastTrend) ?? 0;
  const candidate = currentP16Candidate(p16);
  const entryThreshold = numeric(config?.p16EntryThreshold ?? config?.entryThreshold) ?? 0.5;
  // lastLowProbability is deliberately ignored unless the latest M15 is a
  // candidate and P3 is UP. Otherwise an old probability would look live.
  const currentProbability = candidate.low && trend === 1 ? numeric(p16?.lastLowProbability) : undefined;
  const modelReady = numeric(p16?.lastModelYear) !== undefined;
  const pendingProbability = numeric(p16?.pendingEntry?.probability);
  const probability = pendingProbability ?? numeric(p16?.entryProbability) ?? currentProbability;
  const probabilityMet = Boolean(p16?.active || p16?.pendingEntry || (currentProbability !== undefined && currentProbability >= entryThreshold));
  return [
    {
      label: "P16 model warm-up",
      value: modelReady ? `Model ${p16.lastModelYear}` : "Collecting M15 history",
      status: modelReady ? "READY" : "WAITING",
      detail: modelReady ? "The causal feature window and year model are available." : "No P16 entry can be evaluated until feature warm-up completes.",
      tone: modelReady ? "positive" : "warning",
    },
    {
      label: "Confirmed P3 trend",
      value: trend === 1 ? "UP" : trend === -1 ? "DOWN" : "RANGE",
      status: trend === 1 ? "MET" : "BLOCKED",
      detail: trend === 1 ? "The long-only P16 trend gate is satisfied." : "A new P16 long entry requires confirmed P3 trend UP.",
      tone: trend === 1 ? "positive" : "negative",
    },
    {
      label: "Current P16 low candidate",
      value: candidate.low ? "Observed on latest M15" : "Not observed",
      status: candidate.low ? "MET" : "WAITING",
      detail: candidate.low ? "Latest finalized M15 low reached the prior 16-bar low." : "Waiting for a finalized M15 bar to reach the prior 16-bar low.",
      tone: candidate.low ? "positive" : "warning",
    },
    {
      label: "P16 entry probability",
      value: probability === undefined ? "Not evaluated now" : probability.toFixed(4),
      status: probabilityMet ? "MET" : currentProbability === undefined ? "WAITING" : "BLOCKED",
      detail: `Required ≥ ${entryThreshold.toFixed(2)} on a current low candidate while P3 is UP. Historical probabilities are not treated as a current signal.`,
      tone: probabilityMet ? "positive" : currentProbability === undefined ? "warning" : "negative",
    },
    {
      label: "P16 position state",
      value: p16?.active ? `${Number(p16.targetLeverage ?? 0).toFixed(3)}x active` : p16?.pendingEntry ? "Qualified; next M15 open" : "Flat",
      status: p16?.active ? "ACTIVE" : p16?.pendingEntry ? "READY" : "WAITING",
      detail: p16?.pendingEntry ? "The strategy qualified the signal and intentionally waits for the next causal M15 open." : `M15 bars held: ${Number(p16?.barsHeld ?? 0)}.`,
      tone: p16?.active || p16?.pendingEntry ? "positive" : "neutral",
    },
    {
      label: "Current P16 high candidate",
      value: candidate.high ? "Observed on latest M15" : "Not observed",
      status: candidate.high ? "EXIT WATCH" : "INFO",
      detail: "This is an exit condition for an active P16 sleeve, not an entry requirement.",
      tone: candidate.high ? "info" : "neutral",
    },
  ];
}

function frequencyRows(state: any, config: any): Condition[] {
  const momentum = numeric(state?.lastMomentum);
  const min = numeric(config?.frequencyMinAbsMomentum) ?? 0.0001;
  const max = numeric(config?.frequencyMaxAbsMomentum) ?? 0.00075;
  const inBand = momentum !== undefined && Math.abs(momentum) >= min && Math.abs(momentum) < max;
  const range = (numeric(state?.p3Trend) ?? 0) === 0;
  const action = typeof state?.lastAction === "string" ? state.lastAction : undefined;
  return [
    { label: "Latest scheduled anchor", value: state?.lastEvaluatedAnchor ?? "Not evaluated", status: state?.lastEvaluatedAnchor ? "OBSERVED" : "WAITING", detail: "Entries are evaluated only at 06:30, 09:00, 11:30, 14:00 and 16:30 UTC.", tone: state?.lastEvaluatedAnchor ? "info" : "warning" },
    { label: "Causal 1h momentum band", value: pct(momentum), status: inBand ? "MET" : momentum === undefined ? "WAITING" : "BLOCKED", detail: `Requires ${(min * 100).toFixed(3)}% ≤ |momentum| < ${(max * 100).toFixed(3)}%.`, tone: inBand ? "positive" : momentum === undefined ? "warning" : "negative" },
    { label: "Confirmed P3 M15 structure", value: state?.p3Trend === 1 ? "UP" : state?.p3Trend === -1 ? "DOWN" : "RANGE", status: range ? "MET" : "BLOCKED", detail: "The Frequency sleeve trades only when confirmed P3 M15 structure is RANGE.", tone: range ? "positive" : "negative" },
    { label: "Session action map", value: action ? action.toUpperCase() : "Not evaluated", status: action === "skip" ? "BLOCKED" : action ? "MET" : "WAITING", detail: state?.lastDecisionReason ?? "Waiting for the next configured anchor.", tone: action === "skip" ? "negative" : action ? "positive" : "warning" },
    { label: "Frequency position state", value: state?.active ? `${Number(state.lastTargetLeverage ?? 0).toFixed(3)}x active` : "Flat", status: state?.active ? "ACTIVE" : "WAITING", detail: `Timed exits: ${Number(state?.timedExitCount ?? 0)}; stop exits: ${Number(state?.stopExitCount ?? 0)}.`, tone: state?.active ? "positive" : "neutral" },
  ];
}

function momentumRows(state: any, config: any): Condition[] {
  const observation = momentumObservation(state);
  const threshold = numeric(config?.momentumMinAbsMomentum ?? config?.minAbsMomentum) ?? 0.0005;
  const direction = observation.value === undefined ? undefined : observation.value > 0 ? 1 : -1;
  const allowed = direction === undefined ? undefined : direction > 0 ? config?.allowLong !== false : config?.allowShort !== false;
  return [
    {
      label: "Signal window",
      value: observation.ready ? `${observation.day} complete` : "Waiting for start/end bars",
      status: observation.ready ? "MET" : "WAITING",
      detail: `Requires causal prices at ${config?.momentumSignalStartUtc ?? config?.signalStartUtc ?? "07:30"} and ${config?.momentumSignalEndUtc ?? config?.signalEndUtc ?? "08:30"} on the same ${strategyClockLabel(config)} day.`,
      tone: observation.ready ? "positive" : "warning",
    },
    {
      label: "Observed momentum",
      value: pct(observation.value),
      status: observation.value === undefined ? "WAITING" : Math.abs(observation.value) >= threshold ? "MET" : "BLOCKED",
      detail: `Entry requires absolute momentum ≥ ${(threshold * 100).toFixed(4)}%.`,
      tone: observation.value === undefined ? "warning" : Math.abs(observation.value) >= threshold ? "positive" : "negative",
    },
    {
      label: "Direction permission",
      value: direction === undefined ? "Not known yet" : `${direction > 0 ? "LONG" : "SHORT"} ${allowed ? "enabled" : "disabled"}`,
      status: allowed === undefined ? "WAITING" : allowed ? "MET" : "BLOCKED",
      detail: "Direction permission comes from the selected strategy configuration.",
      tone: allowed === undefined ? "warning" : allowed ? "positive" : "negative",
    },
    {
      label: "Momentum position",
      value: state?.active ? `${Number(state?.targetLeverage ?? state?.lastTargetLeverage ?? 0).toFixed(3)}x active` : state?.tradedDay === state?.day ? "Session evaluated" : "Flat / waiting",
      status: state?.active ? "ACTIVE" : state?.tradedDay === state?.day ? "EVALUATED" : "WAITING",
      detail: state?.active ? "The sleeve has an open target until stop or scheduled exit." : `Entry is evaluated at ${config?.momentumEntryUtc ?? config?.entryUtc ?? "08:30"} ${strategyClockLabel(config)}.`,
      tone: state?.active ? "positive" : state?.tradedDay === state?.day ? "info" : "neutral",
    },
  ];
}

function momentumInputRows(state: any, config: any): Condition[] {
  const observation = momentumObservation(state);
  return [
    {
      label: "Session observation window",
      value: observation.ready ? `${observation.day} complete` : "Waiting for start/end bars",
      status: observation.ready ? "READY" : "WAITING",
      detail: `Uses causal prices at ${config?.momentumSignalStartUtc ?? config?.signalStartUtc ?? "07:30"} and ${config?.momentumSignalEndUtc ?? config?.signalEndUtc ?? "08:30"} ${strategyClockLabel(config)}.`,
      tone: observation.ready ? "positive" : "warning",
    },
    {
      label: "Observed momentum (input only)",
      value: pct(observation.value),
      status: observation.value === undefined ? "WAITING" : "INPUT",
      detail: "Momentum is supplied to Regime Consensus and Near-Miss. Its sign or magnitude never creates a portfolio target by itself.",
      tone: observation.value === undefined ? "warning" : "info",
    },
  ];
}

function regimeRows(state: any, config: any): Condition[] {
  const decision = state?.lastDecision;
  const samples = numeric(state?.features?.finalizedSamples) ?? 0;
  const required = numeric(config?.regimeMinimumFeatureSamples ?? config?.minimumFeatureSamples) ?? 384;
  const minParticipation = numeric(config?.regimeMinParticipation ?? config?.minParticipation) ?? 0.4;
  const minConsensus = numeric(config?.regimeMinConsensus ?? config?.minConsensus) ?? 0.7;
  const participation = numeric(decision?.participation);
  const confidence = numeric(decision?.confidence);
  const direction = numeric(decision?.direction) ?? 0;
  const snapshotValues = decision?.regimeSnapshot ? Object.values(decision.regimeSnapshot).map(numeric) : [];
  const unanimousOpposition = direction !== 0 && snapshotValues.length === 7 && snapshotValues.every((value) => value === -direction);
  const oppositionVetoEnabled = config?.regimeRejectUnanimousSnapshotOpposition !== false;
  return [
    {
      label: "Regime feature warm-up",
      value: `${samples} / ${required} M15`,
      status: samples >= required ? "READY" : "WAITING",
      detail: "EMA and P3 regime votes use finalized M15 candles only.",
      tone: samples >= required ? "positive" : "warning",
    },
    {
      label: "Rule participation",
      value: participation === undefined ? "Not evaluated" : `${(participation * 100).toFixed(1)}%`,
      status: participation === undefined ? "WAITING" : participation >= minParticipation ? "MET" : "BLOCKED",
      detail: `Required ≥ ${(minParticipation * 100).toFixed(0)}% of configured rules.`,
      tone: participation === undefined ? "warning" : participation >= minParticipation ? "positive" : "negative",
    },
    {
      label: "Active-vote agreement",
      value: confidence === undefined ? "Not evaluated" : `${(confidence * 100).toFixed(1)}%`,
      status: confidence === undefined ? "WAITING" : confidence >= minConsensus && direction !== 0 ? "MET" : "BLOCKED",
      detail: `Required ≥ ${(minConsensus * 100).toFixed(0)}% agreement among active votes.`,
      tone: confidence === undefined ? "warning" : confidence >= minConsensus && direction !== 0 ? "positive" : "negative",
    },
    {
      label: "Consensus direction",
      value: direction > 0 ? "LONG" : direction < 0 ? "SHORT" : "FLAT",
      status: direction === 0 ? "BLOCKED" : "MET",
      detail: decision ? `Signed votes ${Number(decision.signedScore ?? 0)} / ${Number(decision.activeVotes ?? 0)}.` : "Evaluated once at the configured session entry time.",
      tone: direction > 0 ? "positive" : direction < 0 ? "negative" : "neutral",
    },
    ...(oppositionVetoEnabled ? [{
      label: "Unanimous regime-opposition veto",
      value: decision ? unanimousOpposition ? "Entry rejected" : "No unanimous opposition" : "Not evaluated",
      status: decision ? unanimousOpposition ? "BLOCKED" : "MET" : "WAITING",
      detail: "A new Regime entry is rejected only when all seven causal regime states oppose the proposed direction.",
      tone: decision ? unanimousOpposition ? "negative" : "positive" : "warning",
    } as Condition] : []),
    {
      label: "Regime Consensus position",
      value: state?.active ? `${Number(state?.lastTargetLeverage ?? 0).toFixed(3)}x active` : state?.tradedDay === state?.day ? "Session evaluated" : "Flat / waiting",
      status: state?.active ? "ACTIVE" : state?.tradedDay === state?.day ? "EVALUATED" : "WAITING",
      detail: state?.active ? "The sleeve remains active until its stop or scheduled exit." : "A completed evaluation does not imply that participation and agreement passed.",
      tone: state?.active ? "positive" : state?.tradedDay === state?.day ? "info" : "neutral",
    },
  ];
}

function nearMissRows(state: any, p16: any, config: any, combined: boolean): Condition[] {
  const observation = momentumObservation(combined ? state?.momentum : state);
  const momentum = observation.value;
  const threshold = numeric(combined ? config?.momentumMinAbsMomentum : config?.mainMomentumMinAbsMomentum) ?? 0.0005;
  const longMin = numeric(combined ? config?.nearLongMinAbsMomentum : config?.longMinAbsMomentum) ?? 0.0003;
  const longMax = numeric(combined ? config?.nearLongMaxAbsMomentum : config?.longMaxAbsMomentum) ?? 0.0005;
  const shortMin = numeric(combined ? config?.nearShortMinAbsMomentum : config?.shortMinAbsMomentum) ?? (combined ? 0.0003 : 0.00025);
  const shortMax = numeric(combined ? config?.nearShortMaxAbsMomentum : config?.shortMaxAbsMomentum) ?? 0.0005;
  const absolute = momentum === undefined ? undefined : Math.abs(momentum);
  const longBand = momentum !== undefined && momentum > 0 && absolute! >= longMin && absolute! < longMax;
  const shortBand = momentum !== undefined && momentum < 0 && absolute! >= shortMin && absolute! < shortMax;
  const trend = numeric(combined ? state?.marketFeatures?.lastTrend : p16?.features?.lastTrend) ?? 0;
  const p16Flat = !p16?.active && !p16?.pendingEntry && Math.abs(Number(p16?.targetLeverage ?? 0)) < 1e-12;
  const regimeFlat = !combined || (!state?.regimeConsensus?.active && Math.abs(Number(state?.regimeConsensus?.lastTargetLeverage ?? 0)) < 1e-12);
  const enabled = !combined || config?.nearMissEnabled !== false;
  const nearState = combined ? state?.nearMiss : state;
  const regimeDecision = combined ? state?.regimeConsensus?.lastDecision : undefined;
  const structuralStates = regimeDecision?.day === observation.day && regimeDecision?.regimeSnapshot
    ? Object.entries(regimeDecision.regimeSnapshot)
        .filter(([name]) => name !== "p3_ema64_192")
        .map(([, value]) => numeric(value))
    : [];
  const bearishConfirmations = structuralStates.filter((value) => value === -1).length;
  const requiredBearish = numeric(config?.nearShortMinBearishRegimes) ?? 1;
  const shortStructureReady = structuralStates.length === 6 && bearishConfirmations >= requiredBearish;
  return [
    {
      label: "Near-Miss signal window",
      value: observation.ready ? `${observation.day} complete` : "Waiting for start/end bars",
      status: observation.ready ? "MET" : "WAITING",
      detail: `Near-Miss is evaluated only after the causal 07:30–08:30 ${strategyClockLabel(config)} observation window.`,
      tone: observation.ready ? "positive" : "warning",
    },
    combined ? {
      label: "Regime Consensus owns no slot",
      value: regimeFlat ? "Regime flat" : "Regime active",
      status: regimeFlat ? "MET" : "BLOCKED",
      detail: "Near-Miss may use the momentum observation only when Regime Consensus did not claim the session slot.",
      tone: regimeFlat ? "positive" : "negative",
    } : {
      label: "Main Momentum relinquishes slot",
      value: pct(momentum),
      status: absolute === undefined ? "WAITING" : absolute < threshold ? "MET" : "BLOCKED",
      detail: `Near-Miss requires |momentum| < ${(threshold * 100).toFixed(4)}%; otherwise main Momentum owns the signal.`,
      tone: absolute === undefined ? "warning" : absolute < threshold ? "positive" : "negative",
    },
    ...(combined ? [{
      label: "Near-Miss sleeve",
      value: enabled ? "Enabled" : "Disabled",
      status: enabled ? "MET" : "BLOCKED",
      detail: "This switch is persisted in the bot strategy configuration and requires a restart to change.",
      tone: enabled ? "positive" : "negative",
    } as Condition] : []),
    {
      label: "Confirmed P3 structure",
      value: trend === 1 ? "UP" : trend === -1 ? "DOWN" : "RANGE",
      status: trend === 0 ? "MET" : "BLOCKED",
      detail: "Near-Miss entry requires confirmed P3 structure RANGE.",
      tone: trend === 0 ? "positive" : "negative",
    },
    ...(!combined ? [{
      label: "P16 owns no slot",
      value: p16Flat ? "P16 flat" : p16?.pendingEntry ? "P16 pending entry" : "P16 active",
      status: p16Flat ? "MET" : "BLOCKED",
      detail: "Near-Miss and P16 are mutually exclusive at entry.",
      tone: p16Flat ? "positive" : "negative",
    } as Condition] : []),
    {
      label: "Near-Miss momentum band",
      value: longBand ? `LONG band (${pct(momentum)})` : shortBand ? `SHORT band (${pct(momentum)})` : momentum === undefined ? "Not observed" : `Outside bands (${pct(momentum)})`,
      status: momentum === undefined ? "WAITING" : longBand || shortBand ? "MET" : "BLOCKED",
      detail: `LONG ${(longMin * 100).toFixed(3)}–${(longMax * 100).toFixed(3)}%; SHORT -${(shortMax * 100).toFixed(3)}–-${(shortMin * 100).toFixed(3)}%.`,
      tone: momentum === undefined ? "warning" : longBand || shortBand ? "positive" : "negative",
    },
    ...(combined ? [{
      label: "Near-Miss SHORT structure confirmation",
      value: shortBand
        ? structuralStates.length === 6
          ? `${bearishConfirmations} / ${requiredBearish} bearish`
          : "Current-day snapshot unavailable"
        : "Applies to SHORT entries only",
      status: shortBand ? shortStructureReady ? "MET" : "BLOCKED" : "N/A",
      detail: `A SHORT Near-Miss requires at least ${requiredBearish} bearish state among the six causal structural EMA regimes from the same strategy day.`,
      tone: shortBand ? shortStructureReady ? "positive" : "negative" : "neutral",
    } as Condition] : []),
    {
      label: "Near-Miss position",
      value: nearState?.active ? `${Number(nearState.targetLeverage ?? 0).toFixed(3)}x active` : "Flat",
      status: nearState?.active ? "ACTIVE" : "WAITING",
      detail: nearState?.active ? "The Near-Miss sleeve currently owns the portfolio target." : "All entry gates must be green at the configured entry time.",
      tone: nearState?.active ? "positive" : "neutral",
    },
  ];
}

function ConditionGrid({ title, rows }: { title: string; rows: Condition[] }) {
  return (
    <Stack gap="2">
      <Text fontSize="10px" color="fg.muted" fontWeight="800" textTransform="uppercase" letterSpacing=".08em">{title}</Text>
      <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)" }} gap="2">
        {rows.map((row) => <ConditionCard key={row.label} row={row} />)}
      </Grid>
    </Stack>
  );
}

function ConditionCard({ row }: { row: Condition }) {
  const styles: Record<Tone, { bg: string; border: string; color: string }> = {
    positive: { bg: "rgba(16,185,129,.07)", border: "rgba(16,185,129,.28)", color: "status.positive" },
    negative: { bg: "rgba(244,63,94,.07)", border: "rgba(244,63,94,.28)", color: "status.negative" },
    warning: { bg: "rgba(245,158,11,.07)", border: "rgba(245,158,11,.28)", color: "status.warning" },
    info: { bg: "rgba(59,130,246,.07)", border: "rgba(59,130,246,.28)", color: "status.info" },
    neutral: { bg: "bg.subtle", border: "border.default", color: "fg.muted" },
  };
  const style = styles[row.tone];
  return (
    <Box bg={style.bg} borderWidth="1px" borderColor={style.border} borderRadius="4px" p="3">
      <Flex justify="space-between" gap="2" align="start">
        <Text fontSize="10px" color="fg.muted">{row.label}</Text>
        <Text fontSize="9px" color={style.color} fontWeight="850" letterSpacing=".05em">{row.status}</Text>
      </Flex>
      <Text mt="1" fontSize="xs" fontWeight="750" color={style.color}>{row.value}</Text>
      <Text mt="1.5" fontSize="9px" color="fg.subtle" lineHeight="1.45">{row.detail}</Text>
    </Box>
  );
}

export function StrategyEntryDiagnostics({ bot }: { bot: BotRecord }) {
  const state = (bot as any).runtime?.strategyState;
  const savedConfig = (bot as any).strategyConfig ?? {};
  const config = bot.strategy === "xau-multi-edge-3-v1" && !savedConfig.strategyTimeZone
    ? { ...savedConfig, strategyTimeZone: "Pepperstone/Server" }
    : savedConfig;
  if (!state) return <Text color="fg.muted" fontSize="xs">No strategy event has been persisted yet.</Text>;

  const targets: Condition[] = bot.strategy === "xau-session-momentum-v1"
    ? [target("Strategy target", state.lastTargetLeverage)]
    : bot.strategy === "xau-near-miss-range-v1"
      ? [target("Near-Miss target", state.targetLeverage)]
      : bot.strategy === "xau-regime-consensus-session-v1"
        ? [target("Regime Consensus target", state.lastTargetLeverage)]
        : bot.strategy === "xau-multi-edge-3-v1"
          ? [
            target("Aggregate target", state.lastAggregateTarget),
            target("Regime Consensus target", state.regimeConsensus?.lastTargetLeverage),
            target("Near-Miss target", state.nearMiss?.targetLeverage),
          ]
          : [
          target("Aggregate target", state.lastAggregateTarget),
          target("Momentum target", state.momentum?.targetLeverage),
          target("Frequency target", state.frequency?.lastTargetLeverage),
          ];

  const supported = ["xau-session-momentum-v1", "xau-regime-consensus-session-v1", "xau-multi-edge-portfolio-v1", "xau-near-miss-range-v1", "xau-multi-edge-3-v1"].includes(bot.strategy);
  if (!supported) return <Box as="pre" whiteSpace="pre-wrap" overflowWrap="anywhere" fontSize="10px" fontFamily="mono" color="fg.muted">{JSON.stringify(state, null, 2)}</Box>;

  return (
    <Stack gap="4">
      <Flex gap="2" flexWrap="wrap" align="center">
        <Text fontSize="10px" color="fg.subtle" mr="1">Latest causal data:</Text>
        <StatusPill tone="positive">MET / ACTIVE</StatusPill>
        <StatusPill tone="warning">WAITING DATA</StatusPill>
        <StatusPill tone="negative">BLOCKED NOW</StatusPill>
        {(bot.strategy === "xau-multi-edge-3-v1" || config.strategyTimeZone) && (
          <StatusPill tone="info">CLOCK · {strategyClockLabel(config)}</StatusPill>
        )}
      </Flex>
      <ConditionGrid title="Current targets" rows={targets} />
      {bot.strategy === "xau-multi-edge-3-v1" && (
        <ConditionGrid title="Loss-streak risk governor" rows={lossStreakGovernorRows(state, config)} />
      )}
      {bot.strategy === "xau-session-momentum-v1" && <ConditionGrid title="Session Momentum entry gates" rows={momentumRows(state, config)} />}
      {bot.strategy === "xau-regime-consensus-session-v1" && <>
        <ConditionGrid title="Momentum observation" rows={momentumInputRows(state, config)} />
        <ConditionGrid title="Regime Consensus entry gates" rows={regimeRows(state, config)} />
      </>}
      {bot.strategy === "xau-multi-edge-portfolio-v1" && <>
        <ConditionGrid title="Session Momentum entry gates" rows={momentumRows(state.momentum, config)} />
        <ConditionGrid title="Range Session Frequency entry gates" rows={frequencyRows(state.frequency, config)} />
      </>}
      {bot.strategy === "xau-near-miss-range-v1" && <>
        <ConditionGrid title="Near-Miss entry gates" rows={nearMissRows(state, state.p16Gate, config, false)} />
        <ConditionGrid title="P16 shadow-gate status" rows={p16Rows(state.p16Gate, config)} />
      </>}
      {bot.strategy === "xau-multi-edge-3-v1" && <>
        <ConditionGrid title="Momentum observation" rows={momentumInputRows(state.momentum, config)} />
        <ConditionGrid title="Regime Consensus entry gates" rows={regimeRows(state.regimeConsensus, config)} />
        <ConditionGrid title="Near-Miss entry gates" rows={nearMissRows(state, undefined, config, true)} />
      </>}
      <Text fontSize="9px" color="fg.subtle">Colors show whether a new entry could qualify from the latest persisted causal data. Strategy timing remains authoritative; green gates never bypass the configured entry time.</Text>
    </Stack>
  );
}
